import { replaceAgentMessage, finishAgentMessages, failAgentMessages, upsertAgentToolMessage, agentCompletionMessage } from 'services/agentChatState';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Drawer, Fab, Typography, useTheme, Chip, Stack, Container, useMediaQuery } from '@mui/material';
// header icons moved into components
// header icons moved into components
// OpenInNewIcon removed from here; used inside ChatMessage
import { MessageOutlined } from '@ant-design/icons';
import SimpleBar from 'components/third-party/SimpleBar';
// moved to HistoryDrawer component
import {
  startAgentStream,
  openAgentStream,
  fetchChatThreads,
  fetchChatThread,
  stopAgentStream,
  fetchOrchestrateStatus,
  subscribeAgentStream,
  shareChatThread
} from 'services/agentService';
import { setChatContext, clearChatContexts, removeChatContext, setChatOpen, setChatFullscreen } from 'store/reducers/chatContext';
import { exportChatHistoryDocx } from 'utils/exportChatDocx';
import ReportViewer from 'components/chat/ReportViewer';
import AgentReportPane from 'components/chat/AgentReportPane';
import ContextMessage from './ChatSidebar/components/ContextMessage';
// Markdown utilities and boundary are used within extracted components
import ChatHeader from './ChatSidebar/components/ChatHeader';
import FullscreenChatHeader from './ChatSidebar/components/FullscreenChatHeader';
import SelectedContextsAlert from './ChatSidebar/components/SelectedContextsAlert';
import ContextDetailsPopover from './ChatSidebar/components/ContextDetailsPopover';
import ShareConfirmationDialog from './ChatSidebar/components/ShareConfirmationDialog';
import ShareSuccessDialog from './ChatSidebar/components/ShareSuccessDialog';

import MessageList from './ChatSidebar/components/MessageList';
import ChatInputBar from './ChatSidebar/components/ChatInputBar';
import HistoryDrawer from './ChatSidebar/components/HistoryDrawer';
import { createHandleSendMessage } from './ChatSidebar/actions/handleSendMessage';
import Button from '@mui/material/Button';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import HelpOutline from '@mui/icons-material/HelpOutline';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';

// (moved) MarkdownErrorBoundary and markdown utils are now in ./ChatSidebar/components and ./ChatSidebar/utils

/* ChatMessage moved to ./ChatSidebar/components/ChatMessage */

/* ChatMessage moved to ./ChatSidebar/components/ChatMessage */

const CHAT_AUTO_SCROLL_THRESHOLD = 120;

const isNearScrollBottom = (node) => {
  if (!node) return true;
  const { scrollHeight = 0, scrollTop = 0, clientHeight = 0 } = node;
  if (scrollHeight <= clientHeight) return true;
  return scrollHeight - scrollTop - clientHeight <= CHAT_AUTO_SCROLL_THRESHOLD;
};

const scrollNodeToBottom = (node, behavior = 'auto') => {
  if (!node) return;
  const top = node.scrollHeight || 0;
  if (typeof node.scrollTo === 'function') {
    node.scrollTo({ top, behavior });
    return;
  }
  node.scrollTop = top;
};

const ChatSidebar = ({
  onToggle,
  forceVisible = false,
  forceFullscreen = false,
  embedded = false,
  inlineReport = false,
  externalReport = false,
  onExternalReportOpen,
  onExternalTogglePane,
  externalPaneOpen = false,
  externalHasReport = false,
  initialThreadId = null
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);

  // Get menu state for calculating input position
  const menu = useSelector((state) => state.menu);
  const { drawerOpen } = menu;
  const isAgentPage = location.pathname.startsWith('/agent');

  // Track history menu width dynamically using MutationObserver
  const [historyMenuWidth, setHistoryMenuWidth] = useState(200);

  useEffect(() => {
    // Skip on mobile - history menu doesn't render
    if (isMobile || !embedded || !isAgentPage) {
      setHistoryMenuWidth(0);
      return;
    }

    // Detect if history menu is visible by checking DOM (desktop only)
    const detectHistoryMenuWidth = () => {
      const boxes = Array.from(document.querySelectorAll('[class*="MuiBox"], [class*="Box"]'));
      const historyMenu = boxes.find((el) => {
        const styles = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        // Desktop only: look for menu after main drawer (left > 50)
        const isDesktopHistoryMenu = rect.left > 50 && rect.left < 300 && rect.width >= 180 && rect.width <= 220;

        return (
          styles.position === 'fixed' &&
          rect.top === 0 &&
          rect.height > 500 && // Full height menu
          isDesktopHistoryMenu
        );
      });

      if (historyMenu) {
        const width = historyMenu.getBoundingClientRect().width;
        setHistoryMenuWidth(Math.round(width));
      } else {
        // Not found or minimized
        setHistoryMenuWidth(0);
      }
    };

    detectHistoryMenuWidth();

    // Use MutationObserver to detect layout changes
    const observer = new MutationObserver(detectHistoryMenuWidth);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Also listen to clicks (for minimize button)
    document.addEventListener('click', detectHistoryMenuWidth);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', detectHistoryMenuWidth);
    };
  }, [isMobile, embedded, isAgentPage, drawerOpen]);

  // Calculate total left offset (main drawer + history menu)
  const getInputLeftOffset = () => {
    // On mobile: no offset, input takes full width
    if (isMobile) {
      return 0;
    }
    if (!embedded || !isAgentPage) {
      return 0;
    }
    const mainDrawerWidth = drawerOpen ? 195 : 64;
    return mainDrawerWidth + historyMenuWidth;
  };
  const [originalMessages, setOriginalMessages] = useState([]);
  const [showTyping, setShowTyping] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeStream, setActiveStream] = useState(null);
  const [answerStarted, setAnswerStarted] = useState(false);
  const [hasStreamedContent, setHasStreamedContent] = useState(false);
  const [reasoningBuffer, setReasoningBuffer] = useState(''); // accumulate raw reasoning during stream
  const [turnReasoningSections, setTurnReasoningSections] = useState([]); // [{id, afterMessageId, steps, raw, open}]
  const [imageAttachments, setImageAttachments] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [threadTitle, setThreadTitle] = useState(null);
  const [threadIsPublic, setThreadIsPublic] = useState(false);
  const [threadShareUrl, setThreadShareUrl] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareSuccessDialogOpen, setShareSuccessDialogOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [threadsPage, setThreadsPage] = useState(1);
  const [threadsNumPages, setThreadsNumPages] = useState(1);
  const [threadsTotal, setThreadsTotal] = useState(0);
  const THREADS_PAGE_SIZE = 8;
  const [draftMessage, setDraftMessage] = useState('');
  const [streamingToolMap, setStreamingToolMap] = useState({}); // id -> { tool_name, status, args, tool_result, lastUpdateTs }
  const [streamingToolOrder, setStreamingToolOrder] = useState([]); // preserve order of tools started
  const chatContext = useSelector((state) => state.chatContext.context);
  const chatContexts = useSelector((state) => state.chatContext.contexts);
  // LP Analysis report sources
  const rawAnalysisResponse = useSelector((state) => state.analysisReducer.response);
  const historyResult = useSelector((state) => state.historyReducer.historyResult);
  const extendedAnalysisResults = useSelector((state) => state.extendedAnalysisReducer.extendedAnalysisResults);
  const messagesEndRef = React.useRef(null);

  // Determine current report context presence for UI indication
  const reportOverview =
    (rawAnalysisResponse?.result && rawAnalysisResponse.result[0]) ||
    (historyResult?.answer_data?.result && historyResult.answer_data.result[0]) ||
    null;
  const reportExtended = extendedAnalysisResults || null;
  const hasReportContext = !!(reportOverview || reportExtended);
  // Follow-up suggestions are rendered inline per assistant message; no global state needed
  const drawerMessagesScrollRef = React.useRef(null);
  const fullscreenMessagesScrollRef = React.useRef(null);
  const shouldAutoScrollMessagesRef = React.useRef(true);
  const ignoreProgrammaticScrollRef = React.useRef(false);
  const settleNextAutoScrollRef = React.useRef(false);
  const handleMessagesScroll = React.useCallback((event) => {
    if (ignoreProgrammaticScrollRef.current) return;
    shouldAutoScrollMessagesRef.current = isNearScrollBottom(event.currentTarget);
  }, []);
  const getMessagesScrollNode = React.useCallback(() => {
    if (isFullscreen && fullscreenMessagesScrollRef.current) return fullscreenMessagesScrollRef.current;
    if (!isFullscreen && drawerMessagesScrollRef.current) return drawerMessagesScrollRef.current;
    return null;
  }, [isFullscreen]);
  const enableMessageAutoScroll = React.useCallback((options = {}) => {
    shouldAutoScrollMessagesRef.current = true;
    if (options.settleAfterRender) {
      settleNextAutoScrollRef.current = true;
    }
  }, []);
  const scrollMessagesToBottom = React.useCallback((options = {}) => {
    const scrollOnce = () => {
      if (!shouldAutoScrollMessagesRef.current) return;
      ignoreProgrammaticScrollRef.current = true;
      scrollNodeToBottom(getMessagesScrollNode(), 'auto');
      window.setTimeout(() => {
        ignoreProgrammaticScrollRef.current = false;
      }, 0);
    };

    scrollOnce();
    if (!options.settleAfterRender) return;

    window.setTimeout(scrollOnce, 0);
    window.setTimeout(scrollOnce, 50);
    window.setTimeout(scrollOnce, 150);
  }, [getMessagesScrollNode]);

  const starterSuggestions = [
    {
      text: "Analyze Nike's competitors in the US",
      category: 'Competitive Analysis',
      icon: 'search',
      action: "Analyze Nike's  competitors in the US"
    },
    {
      text: 'Analyze reviews of Semrush',
      category: 'User Research',
      icon: 'users',
      action: 'Analyze reviews of Semrush'
    },
    {
      text: 'Analyze coca-cola.com',
      category: 'Website Analysis',
      icon: 'lightbulb',
      action: 'Analyze coca-cola.com website'
    },
    {
      text: 'What can you analyze?',
      category: 'Platform Info',
      icon: 'help',
      action: 'What can you analyze?'
    }
  ];

  const renderIcon = (name, size = 18) => {
    switch ((name || '').toLowerCase()) {
      case 'search':
        return <SearchOutlined sx={{ fontSize: size }} />;
      case 'help':
        return <HelpOutline sx={{ fontSize: size }} />;
      case 'users':
        return <GroupOutlined sx={{ fontSize: size }} />;
      case 'lightbulb':
        return <LightbulbOutlined sx={{ fontSize: size }} />;
      default:
        return null;
    }
  };

  const handleStarterClick = (suggestion) => {
    try {
      const content = suggestion?.action || suggestion?.text || '';
      if (content) {
        enableMessageAutoScroll({ settleAfterRender: true });
        handleSendMessage(content);
      }
    } catch (e) {
      // ignore
    }
  };
  const formatDateTime = (iso) => {
    try {
      const d = new Date(iso);
      // Example: Oct 13, 2025, 16:13
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return String(iso || '');
    }
  };

  // Popover for viewing current context details
  const [contextAnchorEl, setContextAnchorEl] = useState(null);
  const [contextView, setContextView] = useState('report'); // 'report' | 'selected'
  const [contextAnchorFrom, setContextAnchorFrom] = useState('footer'); // 'header' | 'footer'
  const [reportData, setReportData] = useState(null);
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState(null);

  // Debug state changes removed
  const handleOpenContext =
    (type, from = 'footer') =>
    (event) => {
      setContextView(type);
      setContextAnchorFrom(from);
      setContextAnchorEl(event.currentTarget);
    };

  // Ensure we exit fullscreen first (if needed) before opening report viewer
  const openReportView = (data, type) => {
    // Always drop local fullscreen first so the chat minimizes immediately
    if (!inlineReport) {
      setIsFullscreen(false);
      setIsOpen(true);
    }

    // External report flow: delegate AFTER ensuring local fullscreen is off
    if (externalReport && typeof onExternalReportOpen === 'function') {
      onExternalReportOpen({ reportData: data, reportType: type });
      return;
    }
    setReportData(data);
    setCurrentReportType(type);
    setReportViewerOpen(true);
  };

  // Safety: whenever a report viewer opens (non-inline), force exit fullscreen so it is visible
  useEffect(() => {
    if (reportViewerOpen && !inlineReport) {
      setIsFullscreen(false);
    }
  }, [reportViewerOpen, inlineReport]);

  // Also guard by data/type presence: when a report payload is set, ensure sidebar mode and drawer open
  useEffect(() => {
    if (reportData && !externalReport && !inlineReport) {
      setIsFullscreen(false);
      setIsOpen(true);
    }
  }, [reportData, externalReport, inlineReport]);

  // Helper function to parse tool_result if it's a string containing JSON
  const parseToolResult = (toolResult) => {
    if (!toolResult) return toolResult;

    // If it's already an object, return as-is
    if (typeof toolResult === 'object' && !Array.isArray(toolResult)) {
      return toolResult;
    }

    // If it's a string, try to parse it
    if (typeof toolResult === 'string') {
      try {
        // Remove markdown code fences if present (```json ... ```)
        let jsonString = toolResult.trim();
        if (jsonString.startsWith('```')) {
          // Remove opening ```json or ```
          jsonString = jsonString.replace(/^```(?:json)?\s*\n?/, '');
          // Remove closing ```
          jsonString = jsonString.replace(/\n?```\s*$/, '');
        }
        // Parse the JSON string
        return JSON.parse(jsonString);
      } catch (e) {
        console.error('Failed to parse tool_result string as JSON:', e);
        // If parsing fails, return the original string
        return toolResult;
      }
    }

    return toolResult;
  };

  // eslint-disable-next-line no-unused-vars
  const handleOpenReport = (toolCall, artifacts, artifactData = null) => {
    // If a report is already open for the same tool+task, do nothing (avoid re-rendering)
    try {
      if (!externalReport && reportViewerOpen && currentReportType === toolCall?.tool && reportData?.analysisData) {
        const currentTaskId =
          reportData.analysisData.task_id ||
          reportData.analysisData?.overview_result?.task_id ||
          reportData.analysisData?.metadata?.task_id ||
          null;
        // Parse tool_result to check task_id if it's a string
        const parsedIncoming = parseToolResult(toolCall?.tool_result);
        const incomingTaskId =
          parsedIncoming?.task_id || parsedIncoming?.overview_result?.task_id || parsedIncoming?.metadata?.task_id || null;
        if (currentTaskId && incomingTaskId && String(currentTaskId) === String(incomingTaskId)) {
          return;
        }
      }
    } catch (e) {
      /* ignore */
    }

    // Check if we have tool_result data directly in toolCall
    if (toolCall?.tool_result) {
      const base = artifactData && typeof artifactData === 'object' ? artifactData : {};
      let resolved = toolCall.tool_result;

      // Parse tool_result if it's a string (JSON wrapped in markdown code blocks)
      resolved = parseToolResult(resolved);

      // If the given tool_result is a string (error) or lacks image/url, try to recover from originalMessages history
      const hasUsable = (res) =>
        !!(res && typeof res === 'object' && (res.b64_image || res.url || (res.result && (res.result.b64_image || res.result.url))));

      // Only try to recover from history if resolved is still not usable AND it's not a valid parsed object for get_who_to_buy_strategy
      const isGetWhoToBuyStrategy = (toolCall.tool || '').replace('functions.', '').toLowerCase() === 'get_who_to_buy_strategy';
      const hasGetWhoToBuyData =
        isGetWhoToBuyStrategy &&
        resolved &&
        typeof resolved === 'object' &&
        (resolved.get || resolved.who || resolved.to || resolved.by || resolved.client || resolved.category);

      if (!hasUsable(resolved) && !hasGetWhoToBuyData && Array.isArray(originalMessages)) {
        try {
          const tname = (toolCall.tool || '').replace('functions.', '').toLowerCase();
          const candidate = [...originalMessages]
            .reverse()
            .find(
              (m) =>
                m &&
                m.role === 'tool' &&
                ((m.metadata && (m.metadata.tool_name || '').toLowerCase() === tname) || (m.tool_name || '').toLowerCase() === tname) &&
                (m.metadata?.tool_result || m.tool_result)
            );
          const candRes = candidate ? candidate.metadata?.tool_result || candidate.tool_result : null;
          const parsedCandRes = parseToolResult(candRes);
          if (
            hasUsable(parsedCandRes) ||
            (isGetWhoToBuyStrategy &&
              parsedCandRes &&
              typeof parsedCandRes === 'object' &&
              (parsedCandRes.get ||
                parsedCandRes.who ||
                parsedCandRes.to ||
                parsedCandRes.by ||
                parsedCandRes.client ||
                parsedCandRes.category))
          ) {
            resolved = parsedCandRes;
          }
        } catch (e) {
          // ignore
        }
      }

      const reportData = {
        ...base,
        analysisData: resolved
      };

      const toolNameNorm = (toolCall.tool || '').replace('functions.', '').toLowerCase();
      let nextReportType = toolNameNorm === 'generate_marketing_image' ? 'picture' : toolCall.tool;

      // Ensure get_who_to_buy_strategy has the functions. prefix for proper detection
      if (toolNameNorm === 'get_who_to_buy_strategy' && !nextReportType.startsWith('functions.')) {
        nextReportType = `functions.${nextReportType}`;
      }

      openReportView(reportData, nextReportType);

      return;
    }

    // Fallback to original logic
    if (artifacts && artifacts.length > 0) {
      const artifact = artifactData || artifacts[0];

      // Find the tool message that contains the actual analysis data
      const toolMessage = originalMessages.find(
        (msg) => msg.role === 'tool' && msg.metadata?.tool_name === toolCall.tool?.replace('functions.', '') && msg.metadata?.tool_result
      );

      if (toolMessage && toolMessage.metadata.tool_result) {
        // Parse tool_result if it's a string
        const parsedToolResult = parseToolResult(toolMessage.metadata.tool_result);
        // Pass the actual analysis data from tool_result
        const data = { ...artifact, analysisData: parsedToolResult };

        // Normalize report type
        let reportType = toolCall.tool;
        const toolNameNorm = (toolCall.tool || '').replace('functions.', '').toLowerCase();
        if (toolNameNorm === 'get_who_to_buy_strategy' && !reportType.startsWith('functions.')) {
          reportType = `functions.${reportType}`;
        }

        openReportView(data, reportType);
      } else {
        // Fallback to just the artifact
        let reportType = toolCall.tool;
        const toolNameNorm = (toolCall.tool || '').replace('functions.', '').toLowerCase();
        if (toolNameNorm === 'get_who_to_buy_strategy' && !reportType.startsWith('functions.')) {
          reportType = `functions.${reportType}`;
        }
        openReportView(artifact, reportType);
      }
    }
  };

  const handleCloseReportViewer = () => {
    setReportViewerOpen(false);
    setReportData(null);
    setCurrentReportType(null);
  };
  const handleCloseContext = () => setContextAnchorEl(null);
  const isContextPopoverOpen = Boolean(contextAnchorEl);

  const buildReportDetailsText = () => {
    const lines = [];
    lines.push('Report Context');
    lines.push('');
    lines.push(`Status: ${hasReportContext ? 'present' : 'absent'}`);
    if (!hasReportContext) return lines.join('\n');
    if (reportOverview) {
      lines.push('');
      lines.push('Overview:');
      try {
        Object.entries(reportOverview).forEach(([key, value]) => {
          const printable = typeof value === 'object' ? JSON.stringify(value) : String(value);
          lines.push(`- ${key}: ${printable}`);
        });
      } catch (e) {
        lines.push(String(reportOverview));
      }
    }
    if (reportExtended) {
      lines.push('');
      lines.push('Extended:');
      try {
        Object.entries(reportExtended).forEach(([key, value]) => {
          const printable = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
          lines.push(`- ${key}: ${printable}`);
        });
      } catch (e) {
        lines.push(String(reportExtended));
      }
    }
    return lines.join('\n');
  };

  const buildSelectedDetailsText = () => {
    const lines = [];
    lines.push('Selected Contexts');
    lines.push('');
    const count = chatContexts?.length || (chatContext ? 1 : 0);
    lines.push(`Count: ${count}`);
    if (chatContexts && chatContexts.length > 0) {
      chatContexts.forEach((ctx, idx) => {
        lines.push(`- #${idx + 1} [${ctx.selectionType || 'card'}]: ${ctx.content}`);
      });
    } else if (chatContext) {
      lines.push(`- #1: ${String(chatContext)}`);
    } else {
      lines.push('- None');
    }
    return lines.join('\n');
  };

  // Build a merged history for inference that includes server/tool messages and local UI messages
  const mergedAllMessages = React.useMemo(() => {
    try {
      // Convert messages (UI format) to backend format for merging
      const local = Array.isArray(messages)
        ? messages.map((m) => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            content: m.content,
            created_at: new Date(m.timestamp || Date.now()).toISOString()
          }))
        : [];
      // originalMessages already contains ALL messages including tool messages
      const base = Array.isArray(originalMessages) ? originalMessages : [];
      return [...base, ...local];
    } catch (e) {
      return originalMessages || [];
    }
  }, [messages, originalMessages]);

  useEffect(() => {
    if (forceVisible) {
      setIsVisible(true);
      setIsOpen(true);
      onToggle(true);
      return;
    }
    const params = new URLSearchParams(location.search);
    const shouldShow = params.get('showChat') === 'true';
    setIsVisible(shouldShow);
    if (!shouldShow) {
      setIsOpen(false);
      onToggle(false);
    }
  }, [location.search, onToggle, forceVisible]);

  useEffect(() => {
    if (typeof forceFullscreen === 'boolean') {
      setIsFullscreen(forceFullscreen);
      // ensure drawer state reflects desired visibility anytime
      if (!forceFullscreen) {
        setIsOpen(true);
      } else {
        setIsOpen(true); // keep open to allow header/expand even with no messages
      }
    }
  }, [forceFullscreen]);

  // Sync local fullscreen flag to Redux so layout can respond reliably
  useEffect(() => {
    dispatch(setChatFullscreen(!!isFullscreen));
  }, [isFullscreen, dispatch]);

  useEffect(() => {
    onToggle(isOpen);
    dispatch(setChatOpen(isOpen));
  }, [isOpen, onToggle, dispatch]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (!shouldAutoScrollMessagesRef.current) return;
    const settleAfterRender = settleNextAutoScrollRef.current;
    settleNextAutoScrollRef.current = false;
    scrollMessagesToBottom({ settleAfterRender });
  }, [messages, scrollMessagesToBottom]);

  // Safety: whenever fullscreen becomes true (from prop or local), ensure all report views are closed
  useEffect(() => {
    if (!isFullscreen) return;
    try {
      if (externalReport && typeof onExternalTogglePane === 'function' && externalPaneOpen) onExternalTogglePane(false);
    } catch (_) {
      /* ignore */
    }
    try {
      if (reportViewerOpen) {
        setReportViewerOpen(false);
        setReportData(null);
        setCurrentReportType(null);
      }
      try {
        window.dispatchEvent(new Event('comp-analysis:clear-highlight'));
      } catch (_) {
        /* ignore */
      }
    } catch (_) {
      /* ignore */
    }
  }, [isFullscreen, externalReport, onExternalTogglePane, externalPaneOpen, reportViewerOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleDownloadChatDocx = async () => {
    try {
      if (!messages || messages.length === 0) return;
      const ts = new Date();
      const fname = `chat-thread-${threadId || ts.toISOString().slice(0, 10)}.docx`;
      await exportChatHistoryDocx(messages, fname);
    } catch (e) {
      // no-op
    }
  };

  const loadThreads = async (pageToLoad = 1) => {
    try {
      setHistoryLoading(true);
      const { items, page, num_pages, total } = await fetchChatThreads({ page: pageToLoad, page_size: THREADS_PAGE_SIZE });
      setThreads(Array.isArray(items) ? items : []);
      setThreadsPage(Number(page) || pageToLoad || 1);
      setThreadsNumPages(Number(num_pages) || 1);
      setThreadsTotal(Number(total) || (Array.isArray(items) ? items.length : 0));
    } catch (e) {
      setThreads([]);
      setThreadsPage(1);
      setThreadsNumPages(1);
      setThreadsTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    await loadThreads(1);
  };

  const closeHistory = () => setHistoryOpen(false);

  const openThread = async (id) => {
    try {
      // Close history drawer immediately to avoid lingering modal backdrop
      setHistoryOpen(false);
      // Ensure any open context popover is closed
      setContextAnchorEl(null);

      // Reset any in-flight tool state and viewers before loading a new thread
      enableMessageAutoScroll({ settleAfterRender: true });
      setActiveStream(null);
      setStreamingToolMap({});
      setStreamingToolOrder([]);
      setReportViewerOpen(false);
      setReportData(null);
      setCurrentReportType(null);

      const data = await fetchChatThread(id);
      // Expect data.messages ordered oldest -> newest
      const rawItems = data?.messages?.items || data?.messages || [];
      const filtered = rawItems.filter((m) => m.role === 'user' || m.role === 'assistant');
      const loadedMessages = filtered.map((m, idx) => ({
        id: Date.now() + idx,
        type: m.role === 'user' ? 'user' : 'ai',
        content: m.content || '',
        timestamp: new Date(m.created_at || m.timestamp || Date.now()).getTime(),
        metadata: m.metadata || {},
        ...(m.role === 'assistant' && m.id ? { backendMessageId: m.id } : {})
      }));
      setMessages(loadedMessages);
      setOriginalMessages(rawItems); // Store original messages with tool messages
      const loadedThreadId = data?.thread?.id || data?.id || id;
      setThreadId(loadedThreadId);
      setThreadTitle(data?.title || data?.thread?.title || null);
      const isPublic = data?.is_public || data?.thread?.is_public || false;
      setThreadIsPublic(isPublic);
      // If thread is public but share_url is missing, construct it from thread_id
      const shareUrl = data?.share_url || data?.thread?.share_url || null;
      setThreadShareUrl(shareUrl || (isPublic && loadedThreadId ? `/shared-reports/${loadedThreadId}` : null));
      setReasoningBuffer('');
      setTurnReasoningSections([]);
      setShowTyping(false);
      // already closed above
      if (!isOpen && !isFullscreen) setIsOpen(true);
      try {
        const nextId = data?.thread?.id || data?.id || id;
        if (nextId) navigate(`/agent/${encodeURIComponent(nextId)}`);
      } catch (_) {
        // ignore navigation errors
      }
      // Nudge layout to recalc (workaround for rare stuck overlays)
      try {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('resize'));
          } catch (_) {
            /* ignore */
          }
        }, 0);
      } catch (_) {
        /* ignore */
      }
    } catch (e) {
      // swallow
    }
  };

  const enterFullscreen = () => {
    // When re-entering fullscreen, ensure any report panes/viewers are closed so nothing overlays chat
    try {
      // Close external agent pane if present
      if (externalReport && typeof onExternalTogglePane === 'function' && externalPaneOpen) onExternalTogglePane(false);
    } catch (_) {
      /* ignore */
    }
    try {
      // Close internal report viewer if present (non-external flow)
      if (reportViewerOpen) {
        setReportViewerOpen(false);
        setReportData(null);
        setCurrentReportType(null);
      }
      // Best-effort: clear any competitor overlays/highlights that might have been rendered
      try {
        window.dispatchEvent(new Event('comp-analysis:clear-highlight'));
      } catch (_) {
        /* ignore */
      }
    } catch (_) {
      /* ignore */
    }
    setIsFullscreen(true);
  };
  const exitFullscreen = () => setIsFullscreen(false);

  const handleRemoveContext = (contextId) => {
    dispatch(removeChatContext(contextId));
  };

  const handleShareClick = () => {
    // If thread is already public, show success dialog directly
    // Otherwise, show confirmation dialog for initial sharing
    if (threadIsPublic) {
      setShareSuccessDialogOpen(true);
    } else {
      setShareDialogOpen(true);
    }
  };

  const handleShareConfirm = async () => {
    if (!threadId) return;
    const newIsPublic = !threadIsPublic;
    setShareLoading(true);
    try {
      const result = await shareChatThread(threadId, newIsPublic);
      setThreadIsPublic(result.is_public || false);
      setThreadShareUrl(result.share_url || null);
      setShareDialogOpen(false);

      // Show success dialog only when sharing (not unsharing)
      if (newIsPublic && result.share_url) {
        setShareSuccessDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to share thread:', error);
      alert(`Failed to ${newIsPublic ? 'share' : 'unshare'} thread: ${error.message || 'Unknown error'}`);
    } finally {
      setShareLoading(false);
    }
  };

  const handleNewChat = () => {
    try {
      activeStream?.close && activeStream.close();
    } catch (_) {
      /* ignore */
    }
    // Clear any pending public thread history when user manually starts a new chat
    try {
      const historyKey = sessionStorage.getItem('askpotUsePublicThreadHistory');
      if (historyKey) {
        sessionStorage.removeItem('askpotUsePublicThreadHistory');
        sessionStorage.removeItem(historyKey);
      }
    } catch (_) {
      // ignore
    }
    setActiveStream(null);
    enableMessageAutoScroll({ settleAfterRender: true });
    setMessages([]);
    setOriginalMessages([]);
    setThreadIsPublic(false);
    setThreadShareUrl(null);
    setShowTyping(false);
    setAnswerStarted(false);
    setHasStreamedContent(false);
    setStreamingToolMap({});
    setStreamingToolOrder([]);
    setTurnReasoningSections([]);
    setReasoningBuffer('');
    setThreadId(null);
    setDraftMessage('');
    setReportViewerOpen(false);
    setReportData(null);
    setCurrentReportType(null);
    dispatch(setChatContext(null));
    dispatch(clearChatContexts());
    try {
      navigate('/agent');
    } catch (_) {
      /* ignore */
    }
  };

  const handleSendMessage = createHandleSendMessage({
    messages,
    setMessages,
    imageAttachments,
    setImageAttachments,
    chatContexts,
    chatContext,
    rawAnalysisResponse,
    historyResult,
    extendedAnalysisResults,
    threadId,
    setThreadId,
    activeStream,
    setActiveStream,
    setShowTyping,
    setAnswerStarted,
    answerStarted,
    setHasStreamedContent,
    startAgentStream,
    openAgentStream,
    setStreamingToolMap,
    setStreamingToolOrder,
    originalMessages,
    setOriginalMessages,
    setTurnReasoningSections,
    setReasoningBuffer,
    hasStreamedContent,
    reasoningBuffer,
    streamingToolMap
  });

  // When the user sends from the input bar, immediately clear the draft
  // so the just-sent message only appears in history, not duplicated in the input.
  const handleInputSendMessage = async (msg) => {
    // Clear the controlled value that feeds PremiumChatInput.externalValue
    setDraftMessage('');
    enableMessageAutoScroll({ settleAfterRender: true });
    // Delegate to the main send handler (which manages streaming, state, etc.)
    return handleSendMessage(msg);
  };

  // Removed threadId-based auto navigation to avoid URL oscillation.
  // When a new thread id is created during streaming, update URL so reload keeps the content
  useEffect(() => {
    try {
      if (!threadId) return;
      if (location.pathname === '/agent') {
        navigate(`/agent/${encodeURIComponent(threadId)}`, { replace: true });
      }
    } catch (_) {
      // ignore navigation errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Load initial thread from prop
  useEffect(() => {
    if (initialThreadId) {
      // Avoid duplicate load if already set
      if (threadId !== initialThreadId) {
        openThread(initialThreadId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialThreadId]);

  // If a thread is open, check if orchestrator is still running and, if so, subscribe to future events
  useEffect(() => {
    let mounted = true;
    const maybeReconnect = async () => {
      try {
        if (!threadId) return;
        // If we are already actively streaming (initial send), do not subscribe
        if (answerStarted || showTyping) return;
        // Avoid duplicate subscriptions if we already have an active stream
        if (activeStream && typeof activeStream.close === 'function') return;
        const status = await fetchOrchestrateStatus(threadId);
        if (!mounted) return;
        if (status && status.running) {
          // Ensure we have a streaming placeholder message
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.type === 'ai' && last.streaming) return prev;
            return [...prev, { id: Date.now() + 1, type: 'ai', content: '', timestamp: Date.now(), streaming: true }];
          });
          setShowTyping(true);
          setAnswerStarted(true);

          let streamFinished = false;
          const es = subscribeAgentStream(threadId, {
            onOpen: () => {},
            onToken: (data) => {
              const tokenText = data?.text ?? data?.delta ?? data?.content ?? data?.message ?? '';
              if (data?.type === 'message_snapshot') {
                setMessages((prev) => replaceAgentMessage(prev, data.message));
                return;
              }
              // Handle structured stream errors (e.g., insufficient credits)
              if (data && data.type === 'error') {
                const code = data.code || data.error || '';
                if (String(code).toLowerCase() === 'insufficient_credits') {
                  // Replace the current streaming placeholder (if any) with an inline paywall card
                  setShowTyping(false);
                  setHasStreamedContent(false);
                  setMessages((prev) => {
                    const last = prev[prev.length - 1];
                    const paywallMsg = {
                      id: Date.now() + 5,
                      type: 'ai',
                      subtype: 'insufficient_credits',
                      timestamp: Date.now(),
                      meta: {
                        available_credits: data.available_credits,
                        required_credits: data.required_credits,
                        tool_name: data.tool_name,
                        user_plan: data.user_plan,
                        is_free_plan:
                          typeof data.is_free_plan === 'boolean'
                            ? data.is_free_plan
                            : String(data.user_plan || '')
                                .trim()
                                .toLowerCase() === 'free'
                      }
                    };
                    if (last && last.type === 'ai' && last.streaming) {
                      const updated = [...prev];
                      updated[updated.length - 1] = paywallMsg;
                      return updated;
                    }
                    return [...prev, paywallMsg];
                  });
                  return;
                }
              }
              if (data && ['tool_started', 'tool_output', 'tool_completed'].includes(data.type)) {
                const toolName = data.tool_name || data.metadata?.tool_name;
                const callId = data.metadata?.tool_call_id;
                if (!toolName || !callId) return;
                let args = data.args;
                if (typeof args === 'string') { try { args = JSON.parse(args); } catch (_) { /* text input */ } }
                setStreamingToolMap((prev) => {
                  const existing = prev[callId] || {};
                  return { ...prev, [callId]: { ...existing, tool_name: toolName, call_id: callId,
                    status: data.type === 'tool_started' ? 'running' : (data.status || data.metadata?.status || (data.result?.error ? 'failed' : 'completed')),
                    args: args || existing.args, startTs: existing.startTs || Date.now(), lastUpdateTs: Date.now(),
                    ...(data.result !== undefined ? { tool_result: data.result } : {})
                  } };
                });
                setStreamingToolOrder((prev) => prev.includes(callId) ? prev : [...prev, callId]);
                if (data.type === 'tool_output') setOriginalMessages((prev) => upsertAgentToolMessage(prev, data));
                return;
              }
              const isReasoningToken = data?.type === 'reasoning';
              if (isReasoningToken) {
                if (!tokenText) return;
                setReasoningBuffer((prev) => prev + tokenText);
                return;
              }
              if (tokenText) {
                setHasStreamedContent(true);
                setMessages((prev) => {
                  const last = prev[prev.length - 1];
                  if (last && last.type === 'ai' && last.streaming) {
                    const updated = [...prev];
                    updated[updated.length - 1] = { ...last, content: (last.content || '') + tokenText };
                    return updated;
                  }
                  return [...prev, { id: Date.now() + 2, type: 'ai', content: tokenText, timestamp: Date.now(), streaming: true }];
                });
              }
            },
            onComplete: (info) => {
              streamFinished = true;
              if (info?.status !== 'cancelled') {
                const message = agentCompletionMessage(info);
                setOriginalMessages((prev) => message.id && prev.some((item) => item.id === message.id) ? prev :
                  [...prev, { ...message, role: 'assistant', created_at: new Date().toISOString() }]);
              }
              setShowTyping(false);
              setMessages((prev) => finishAgentMessages(prev, info));
              setActiveStream(null);
            },
            onError: (error) => {
              streamFinished = true;
              setShowTyping(false);
              setMessages((prev) => failAgentMessages(prev, error));
              setActiveStream(null);
            }
          });

          if (!streamFinished) setActiveStream(es);
        }
      } catch (_) {
        // ignore errors on status/subscribe
      }
    };
    maybeReconnect();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Auto-consume a pending chat message from cookie or public thread history (Agent embedded chat only; consume once)
  const hasConsumedRef = React.useRef(false);
  useEffect(() => {
    try {
      const shouldConsume = !!embedded && (location.pathname.includes('/agent') || !!forceVisible || !!isVisible);
      if (!shouldConsume || hasConsumedRef.current) return;

      // First check for public thread history (only if we're on /agent page and no threadId yet)
      // This ensures we only use it when starting a new thread, not when loading an existing one
      try {
        if (location.pathname === '/agent' && !threadId) {
          const historyKey = sessionStorage.getItem('askpotUsePublicThreadHistory');
          if (historyKey) {
            const stored = sessionStorage.getItem(historyKey);
            if (stored) {
              const parsed = JSON.parse(stored);
              // Check if the stored history is recent (within last 30 minutes) to prevent stale data
              // This allows time for signup/login flows while still preventing very old stale data
              const isRecent = parsed.timestamp && Date.now() - parsed.timestamp < 30 * 60 * 1000;
              if (parsed.newMessage && parsed.newMessage.trim() && isRecent) {
                // Load the public thread history into messages for display
                // Follow the same pattern as openThread: filter out tool messages from display,
                // but store ALL messages (including tools) in originalMessages
                if (parsed.messageHistory && Array.isArray(parsed.messageHistory) && parsed.messageHistory.length > 0) {
                  // Filter to only user/assistant messages for display (same as openThread line 591)
                  const filtered = parsed.messageHistory.filter((m) => m.role === 'user' || m.role === 'assistant');
                  const historyMessages = filtered.map((m, idx) => ({
                    id: Date.now() + idx,
                    type: m.role === 'user' ? 'user' : 'ai',
                    content: m.content || '',
                    // CRITICAL: Use actual created_at timestamp from message (same as openThread line 586)
                    // This ensures ChatMessage can properly match tool messages to assistant messages by timestamp
                    timestamp: new Date(m.created_at || m.timestamp || Date.now()).getTime(),
                    metadata: m.metadata || {},
        ...(m.role === 'assistant' && m.id ? { backendMessageId: m.id } : {})
                  }));
                  setMessages(historyMessages);
                  // Store ALL messages (including tool messages) in originalMessages (same as openThread line 600)
                  setOriginalMessages(parsed.messageHistory);
                }
                // Send the new message (handleSendMessage will check originalMessages first, then sessionStorage as fallback)
                // handleSendMessage will clear sessionStorage after reading it
                hasConsumedRef.current = true;
                enableMessageAutoScroll({ settleAfterRender: true });
                handleSendMessage(parsed.newMessage);
                return;
              } else {
                // Stale or invalid data, clear it
                try {
                  sessionStorage.removeItem('askpotUsePublicThreadHistory');
                  sessionStorage.removeItem(historyKey);
                } catch (_) {
                  // ignore
                }
              }
            }
          }
        }
      } catch (e) {
        // ignore sessionStorage errors, fall through to cookie check
      }

      // Fallback to cookie check
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return '';
      };
      const pending = getCookie('askpotPendingChatMessage');
      if (pending) {
        const decoded = decodeURIComponent(pending);
        // Clear cookie with matching domain to ensure deletion
        try {
          const domain = window.location.hostname === 'localhost' ? 'localhost' : '.askpot.com';
          document.cookie = `askpotPendingChatMessage=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        } catch (_) {
          // fallback clear
          document.cookie = 'askpotPendingChatMessage=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
        if (decoded && decoded.trim() !== '') {
          hasConsumedRef.current = true;
          enableMessageAutoScroll({ settleAfterRender: true });
          handleSendMessage(decoded);
        }
      }
    } catch (e) {
      // no-op
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, forceVisible, location.pathname, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Fab
          color="primary"
          aria-label="open chat"
          onClick={handleToggle}
          sx={{
            position: 'fixed',
            top: '80px',
            right: '24px',
            zIndex: theme.zIndex.drawer + 1
          }}
        >
          <MessageOutlined />
        </Fab>
      )}

      {!isFullscreen && (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={handleToggle}
          variant="persistent"
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              height: '100%',
              boxShadow: theme.customShadows.z1,
              borderLeft: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              transition: 'width 280ms ease'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: 1 }}>
            <ChatHeader
              title="Chat"
              showReportChip={hasReportContext}
              onClickReportChip={handleOpenContext('report', 'header')}
              messagesCount={messages ? messages.length : 0}
              onHistory={openHistory}
              onNewChat={handleNewChat}
              onDownload={handleDownloadChatDocx}
              externalReport={externalReport}
              externalPaneOpen={externalPaneOpen}
              externalHasReport={externalHasReport}
              threadId={threadId}
              isPublic={threadIsPublic}
              shareUrl={threadShareUrl}
              onShare={handleShareClick}
              onExternalTogglePane={onExternalTogglePane}
              onToggleFullscreen={enterFullscreen}
              onClose={handleToggle}
              hideClose={location.pathname.includes('/agent')}
            />
            {(chatContexts.length > 0 || chatContext) && (
              <SelectedContextsAlert
                chatContexts={chatContexts}
                chatContext={chatContext}
                onClearAll={() => {
                  dispatch(setChatContext(null));
                  dispatch(clearChatContexts());
                }}
                onRemoveContext={handleRemoveContext}
              />
            )}
            <SimpleBar
              scrollableNodeProps={{
                ref: drawerMessagesScrollRef,
                onScroll: handleMessagesScroll
              }}
              sx={{
                flexGrow: 1,
                p: { xs: 1, sm: 2 },
                '& .simplebar-content': { height: '100%' },
                '& .simplebar-content-wrapper': { height: '100%' }
              }}
            >
              {/* {chatContexts.map((context) => (
                <ContextMessage key={context.id} context={context} theme={theme} onRemove={handleRemoveContext} />
              ))} */}
              {messages.length === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100%',
                    px: 2
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: (t) => t.shape.borderRadius * 2,
                      bgcolor: theme.palette.background.paper,
                      maxWidth: 420,
                      width: '100%'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center', mb: 1.5 }}>
                      Ask anything to kick off deep research
                    </Typography>

                    <Box
                      sx={{
                        mt: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%'
                      }}
                    >
                      {starterSuggestions.map((s, idx) => (
                        <Button
                          key={idx}
                          variant="outlined"
                          size="small"
                          color="inherit"
                          onClick={() => handleStarterClick(s)}
                          startIcon={renderIcon(s.icon, 16)}
                          sx={{
                            borderRadius: 9999,
                            textTransform: 'none',
                            bgcolor: theme.palette.background.paper,
                            color: 'text.primary',
                            borderColor: theme.palette.divider,
                            '& .MuiSvgIcon-root': { color: 'text.secondary' },
                            '&:hover': { bgcolor: theme.palette.action.hover, borderColor: theme.palette.divider }
                          }}
                        >
                          {s.text}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
              <MessageList
                messages={messages}
                theme={theme}
                mergedAllMessages={mergedAllMessages}
                streamingToolOrder={streamingToolOrder}
                streamingToolMap={streamingToolMap}
                turnReasoningSections={turnReasoningSections}
                onOpenReport={handleOpenReport}
                compactLayout
                onToggleReasoningSection={(id) =>
                  setTurnReasoningSections((prev) => prev.map((s) => (s.id === id ? { ...s, open: !s.open } : s)))
                }
                onClickFollowUp={(q) => {
                  enableMessageAutoScroll({ settleAfterRender: true });
                  handleSendMessage(q);
                }}
                isFullscreen={false}
              />
              <div ref={messagesEndRef} />
            </SimpleBar>
            <Box sx={{ p: 0, borderTop: 'none', flexShrink: 0 }}>
              <Box sx={{ px: { xs: 0, sm: 1.5 }, py: { xs: 0.25, sm: 0.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  {(chatContexts.length > 0 || chatContext) && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={chatContexts.length > 0 ? `${chatContexts.length} Selected` : '1 Selected'}
                      onClick={handleOpenContext('selected')}
                    />
                  )}
                </Stack>
                <ContextDetailsPopover
                  open={isContextPopoverOpen}
                  anchorEl={contextAnchorEl}
                  onClose={handleCloseContext}
                  anchorOrigin={
                    contextAnchorFrom === 'header' ? { vertical: 'bottom', horizontal: 'left' } : { vertical: 'top', horizontal: 'left' }
                  }
                  transformOrigin={
                    contextAnchorFrom === 'header' ? { vertical: 'top', horizontal: 'left' } : { vertical: 'bottom', horizontal: 'left' }
                  }
                  zIndex={theme.zIndex.modal + 5}
                  title={contextView === 'report' ? 'Report Context' : 'Selected Contexts'}
                  content={contextView === 'report' ? buildReportDetailsText() : buildSelectedDetailsText()}
                />
                <ChatInputBar
                  onSendMessage={handleInputSendMessage}
                  showTyping={showTyping}
                  autoFocus
                  placeholder={messages.length === 0 ? 'Analyze semrush’s top 3 competitors and their positioning' : undefined}
                  value={draftMessage}
                  onChangeValue={setDraftMessage}
                  onStop={async () => {
                    try {
                      if (threadId) {
                        await stopAgentStream(threadId);
                      }
                    } finally {
                      try {
                        activeStream?.close && activeStream.close();
                      } catch (_) {
                        /* ignore */ void 0;
                      }
                      setActiveStream(null);
                      setShowTyping(false);
                    }
                  }}
                  compact
                />
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}

      {isFullscreen && (
        <Box
          sx={{
            position: embedded ? 'relative' : 'fixed',
            top: embedded ? undefined : 0,
            right: embedded ? undefined : 0,
            bottom: embedded ? undefined : 0,
            left: embedded ? undefined : 'var(--layout-left-offset, 0px)',
            width: embedded ? '100%' : 'calc(100% - var(--layout-left-offset, 0px))',
            height: '100%',
            bgcolor: theme.palette.background.default,
            zIndex: embedded ? 'auto' : theme.zIndex.modal + 3,
            display: 'flex',
            flexDirection: inlineReport && reportViewerOpen ? 'row' : 'column'
          }}
        >
          {inlineReport && reportViewerOpen && (
            <Box sx={{ flex: 1, minWidth: 0, borderRight: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column' }}>
              <AgentReportPane open={true} onClose={handleCloseReportViewer} reportData={reportData} reportType={currentReportType} />
            </Box>
          )}
          {/* Right pane: chat UI */}
          <Box
            sx={{
              width: inlineReport && reportViewerOpen ? { xs: '100%', md: 420, lg: 520 } : '100%',
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <Box sx={{ p: { xs: 0.5, sm: 1 }, pt: { xs: 8, sm: 1 }, flexShrink: 0 }}>
              <Container maxWidth="md" disableGutters sx={{ px: { xs: 1.5, sm: 2 } }}>
                <FullscreenChatHeader
                  title="Chat"
                  showReportChip={hasReportContext}
                  onClickReportChip={handleOpenContext('report', 'header')}
                  messagesCount={messages ? messages.length : 0}
                  onHistory={openHistory}
                  onNewChat={handleNewChat}
                  onDownload={handleDownloadChatDocx}
                  externalReport={externalReport}
                  onExternalExit={() => {
                    // Only open the external pane if a report is actually available
                    if (externalReport && externalHasReport && typeof onExternalTogglePane === 'function') {
                      onExternalTogglePane(true);
                    }
                    exitFullscreen();
                  }}
                  onExit={exitFullscreen}
                  onClose={handleToggle}
                  hideClose={location.pathname.includes('/agent')}
                  threadId={threadId}
                  isPublic={threadIsPublic}
                  shareUrl={threadShareUrl}
                  onShare={handleShareClick}
                />
              </Container>
            </Box>
            {(chatContexts.length > 0 || chatContext) && (
              <SelectedContextsAlert
                chatContexts={chatContexts}
                chatContext={chatContext}
                onClearAll={() => {
                  dispatch(setChatContext(null));
                  dispatch(clearChatContexts());
                }}
                onRemoveContext={handleRemoveContext}
                showClearHint
              />
            )}
            <Box
              data-testid="fullscreen-chat-scroll"
              ref={fullscreenMessagesScrollRef}
              onScroll={handleMessagesScroll}
              sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', pb: '10px' }}
            >
              <Container
                maxWidth="md"
                disableGutters
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  py: { xs: 1, sm: 2 },
                  pb: { xs: 20, sm: 24 },
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {chatContexts.map((context) => (
                  <ContextMessage key={context.id} context={context} theme={theme} onRemove={handleRemoveContext} />
                ))}
                {messages.length === 0 && (
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                    <Box sx={{ textAlign: 'center', maxWidth: 960, width: '100%', px: 2 }}>
                      <Typography variant="h3" sx={{ mb: 3 }}>
                        Ask anything to start a chat
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gap: 1.5,
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          maxWidth: 668,
                          mx: 'auto'
                        }}
                      >
                        {starterSuggestions.map((s, idx) => (
                          <Box
                            key={idx}
                            component="div"
                            onClick={() => handleStarterClick(s)}
                            role="button"
                            aria-label={s.text}
                            sx={{ textAlign: 'left' }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 4,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background.paper,
                                '&:hover': { bgcolor: theme.palette.action.hover },
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 0.5 }}>
                                {renderIcon(s.icon, 18)}
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  {s.category}
                                </Typography>
                              </Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {s.text}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                <MessageList
                  messages={messages}
                  theme={theme}
                  mergedAllMessages={mergedAllMessages}
                  streamingToolOrder={streamingToolOrder}
                  streamingToolMap={streamingToolMap}
                  turnReasoningSections={turnReasoningSections}
                  onOpenReport={handleOpenReport}
                  compactLayout={false}
                  onToggleReasoningSection={(id) =>
                    setTurnReasoningSections((prev) => prev.map((s) => (s.id === id ? { ...s, open: !s.open } : s)))
                  }
                  onClickFollowUp={(q) => {
                    enableMessageAutoScroll({ settleAfterRender: true });
                    handleSendMessage(q);
                  }}
                  isFullscreen={true}
                />
                <div ref={messagesEndRef} />
              </Container>
            </Box>
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                ...(inlineReport && reportViewerOpen
                  ? {
                      right: 0,
                      width: { xs: '100%', md: 420, lg: 520 }
                    }
                  : isMobile
                  ? {
                      left: 0,
                      right: 0,
                      width: '100%'
                    }
                  : {
                      left: embedded ? getInputLeftOffset() : 'var(--layout-left-offset, 0px)',
                      width: embedded ? `calc(100% - ${getInputLeftOffset()}px)` : 'auto',
                      right: embedded ? undefined : 0
                    }),
                p: 0,
                borderTop: 'none',
                bgcolor: theme.palette.background.default,
                zIndex: embedded ? 11 : theme.zIndex.modal + 4,
                display: 'flex',
                justifyContent: 'center',
                transition: isMobile ? 'none' : 'left 0.3s ease, width 0.3s ease'
              }}
            >
              <Container maxWidth="md" disableGutters sx={{ px: { xs: 1, sm: 3 }, pt: 0, pb: { xs: 0.5, sm: 1 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  {(chatContexts.length > 0 || chatContext) && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={chatContexts.length > 0 ? `${chatContexts.length} Selected` : '1 Selected'}
                      onClick={handleOpenContext('selected')}
                    />
                  )}
                </Stack>
                <ContextDetailsPopover
                  open={isContextPopoverOpen}
                  anchorEl={contextAnchorEl}
                  onClose={handleCloseContext}
                  anchorOrigin={
                    contextAnchorFrom === 'header' ? { vertical: 'bottom', horizontal: 'left' } : { vertical: 'top', horizontal: 'left' }
                  }
                  transformOrigin={
                    contextAnchorFrom === 'header' ? { vertical: 'top', horizontal: 'left' } : { vertical: 'bottom', horizontal: 'left' }
                  }
                  zIndex={theme.zIndex.modal + 5}
                  maxWidth={800}
                  maxHeight={500}
                  title={contextView === 'report' ? 'Report Context' : 'Selected Contexts'}
                  content={contextView === 'report' ? buildReportDetailsText() : buildSelectedDetailsText()}
                />
                <ChatInputBar
                  onSendMessage={handleInputSendMessage}
                  showTyping={showTyping}
                  autoFocus
                  actionsPlacement="right"
                  noTopPadding
                  dense
                  placeholder={messages.length === 0 ? 'Analyze semrush’s top 3 competitors and their positioning' : undefined}
                  value={draftMessage}
                  onChangeValue={setDraftMessage}
                  onStop={async () => {
                    try {
                      if (threadId) {
                        await stopAgentStream(threadId);
                      }
                    } finally {
                      try {
                        activeStream?.close && activeStream.close();
                      } catch (_) {
                        /* ignore */ void 0;
                      }
                      setActiveStream(null);
                      setShowTyping(false);
                    }
                  }}
                  menuZIndex={theme.zIndex.modal + 40}
                  imageAttachments={imageAttachments}
                  onChangeImageAttachments={setImageAttachments}
                />
              </Container>
            </Box>
          </Box>
        </Box>
      )}
      {/* History Drawer (global) */}
      <HistoryDrawer
        open={historyOpen}
        onClose={closeHistory}
        threads={threads}
        loading={historyLoading}
        onOpenThread={openThread}
        formatDateTime={formatDateTime}
        page={threadsPage}
        numPages={threadsNumPages}
        total={threadsTotal}
        pageSize={THREADS_PAGE_SIZE}
        onChangePage={(p) => {
          if (typeof p === 'number' && p > 0 && p !== threadsPage) {
            loadThreads(p);
          }
        }}
      />

      {/* Report Viewer - only when not using external agent pane */}
      {!externalReport && (
        <ReportViewer open={reportViewerOpen} onClose={handleCloseReportViewer} reportData={reportData} reportType={currentReportType} />
      )}

      {/* Share Confirmation Dialog - Show for initial sharing OR for making private */}
      <ShareConfirmationDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onConfirm={handleShareConfirm}
        isPublic={threadIsPublic}
        loading={shareLoading}
      />

      {/* Share Success Dialog */}
      <ShareSuccessDialog
        open={shareSuccessDialogOpen}
        onClose={() => setShareSuccessDialogOpen(false)}
        shareUrl={threadShareUrl || (threadIsPublic && threadId ? `/shared-reports/${threadId}` : null)}
        threadTitle={threadTitle || 'Chat Thread'}
        onMakePrivate={async () => {
          setShareSuccessDialogOpen(false);
          // Show confirmation dialog for making private
          // Set a flag to indicate this is for making private, not initial sharing
          setShareDialogOpen(true);
        }}
        messages={messages}
      />
    </>
  );
};

export default ChatSidebar;
